import React, { Fragment } from "react";
import { makeStyles } from "@material-ui/core";
import { CustomerType, useBookingGroupCard_GetBookingGroupQuery } from "@graphql";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import { Body, ErrorDialog, PlainLink, Spinner } from "@components";
import ItemCard from "@components/ItemCard/ItemCard";
import CustomerChip from "@components/CustomerChip";
import CustomerParents from "@components/CustomerParents";
import { CustomerProfileLocation } from "@navigation/locations/CustomerProfileLocation";
import { ConsultantProfileLocation } from "@navigation/locations/consultant-profile.location";
import { CONSULTANT_SLIDER } from "@navigation";
import ConsultantCard from "@consultants/ConsultantCard/ConsultantCard";
import NationellaChip from "@common/NationellaChip";

type Props = {
  bookingGroupId: string;
  onOpen?: () => any;
  onEdit?: () => any;
};

export function BookingGroupCard(props: Props) {
  const classes = useStyles({});
  const { data, loading, error } = useBookingGroupCard_GetBookingGroupQuery({
    variables: { id: props.bookingGroupId },
    fetchPolicy: "no-cache"
  });

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorDialog open message={error.toString()} />;
  }

  const item = data.getBookingGroup;

  const openCustomer = (id) =>
    store().navigation.open("customer_slider", new CustomerProfileLocation(id));

  const openConsultant = (id) =>
    store().navigation.open(CONSULTANT_SLIDER, new ConsultantProfileLocation(id));

  const subheading = (
    <div>
      <Body style={{ fontWeight: "bold" }}>
        {item.hospital && item.hospital.Address ? item.hospital.Address.Region : ""}
      </Body>
      <CustomerParents
        parents={item.hospital.Parents}
        onClick={(e, id) => {
          e.preventDefault();
          openCustomer(id);
        }}
        afterLabel={
          <span style={{ marginLeft: 8 }}>
            <CustomerChip type={CustomerType.Region} />
          </span>
        }
      />
    </div>
  );

  const departments = () => {
    if (!item?.department) {
      return null;
    }

    return (
      <div className={classes.departmentList}>
        <Fragment>
          <PlainLink
            className={classes.departmentLink}
            key={item.department._id}
            onClick={(e) => {
              e.preventDefault();
              openCustomer(item.department._id);
            }}
          >
            {item.department.Name}
          </PlainLink>
          <CustomerChip type={CustomerType.Department} />
        </Fragment>
      </div>
    );
  };

  const subtitle = (
    <div>
      {departments()}

      <Body style={{ fontWeight: "bold" }}>
        {locale.Expertise}:{" "}
        <span style={{ fontWeight: "normal" }}>{item.hospital.AreasOfExpertise}</span> s
      </Body>

      <br />
      <Body style={{ fontWeight: "bold" }}>
        {locale.CallNumber}: <span style={{ fontWeight: "normal" }}>{"n/a"}</span>
      </Body>
      <br />
      <Body style={{ fontWeight: "bold" }}>
        {locale.RecruitingPortalType}:{" "}
        <span style={{ fontWeight: "normal" }}>
          {locale.recruitingPortalType[item.hospital?.RecruitingPortalType] ?? "n/a"}
        </span>
      </Body>
    </div>
  );

  const chips = (
    <>
      <CustomerChip type={CustomerType.Hospital} />
      {item.isNationalProcurement && <NationellaChip />}
    </>
  );

  return (
    <ItemCard
      entity={{ Name: item.hospital.Name }}
      title={item.hospital.Name}
      chips={chips}
      subTitle={subtitle}
      subheading={subheading}
      onTitleClick={(e) => {
        e.preventDefault();
        openCustomer(item.hospital._id);
      }}
      withRightPane={
        <ConsultantCard
          consultantId={item.consultant._id}
          details={[]}
          flat
          style={{ background: "#fff" }}
          onEdit={props.onEdit}
          onTitleClick={(e) => {
            e.preventDefault();
            openConsultant(item.consultant._id);
          }}
        />
      }
      onClick={props.onOpen}
    />
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  avatar: {
    minWidth: 48
  },

  nameMarksWrapper: {
    alignItems: "center",
    display: "flex"
  },

  departmentList: {
    ...theme.mixins.childrenMarginRight(theme.spacing(1 / 2))
  },

  departmentLink: {
    display: "inline"
  },

  header: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(2)
  },

  headerText: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 10
  }
}));
